import { create } from 'zustand';

function sameEditingLock(
    current,
    next
) {

    return (
        current?.userId === next?.userId &&
        current?.userName === next?.userName &&
        current?.socketId === next?.socketId
    );

}

function sameChildren(
    current = [],
    next = []
) {

    if (current.length !== next.length) {
        return false;
    }

    return current.every(
        (tripId, index) =>
            tripId === next[index]
    );

}

function sameNode(
    current,
    next
) {

    if (!current) {
        return false;
    }

    return (
        current.tripId === next.tripId &&
        current.parentTripId === next.parentTripId &&
        current.codigoIntegracao === next.codigoIntegracao &&
        current.originalVehicle === next.originalVehicle &&
        current.newVehicle === next.newVehicle &&
        current.resolved === next.resolved &&
        current.startAt === next.startAt &&
        current.endAt === next.endAt &&
        current.timeCaught === next.timeCaught &&
        current.canAdjustNow === next.canAdjustNow &&
        sameEditingLock(
            current.editingLock,
            next.editingLock
        ) &&
        sameChildren(
            current.children,
            next.children
        )
    );

}

function createNode({
    tripId,
    parentTripId = null,
    codigoIntegracao,
    originalVehicle,
    newVehicle,
    startAt,
    endAt,
    timeCaught,
    canAdjustNow = true,
    editing = null
}) {

    return {

        tripId,

        parentTripId,

        codigoIntegracao,

        originalVehicle,

        newVehicle:
            originalVehicle === newVehicle
                ? null
                : newVehicle,

        resolved: false,

        children: [],

        startAt,

        endAt,

        timeCaught,

        canAdjustNow,

        editingLock: editing ?? null

    };

}

export const useVehicleConflictStore = create((set, get) => ({

    vehicleChangeMotive: 0,

    loadingConflicts: false,

    nodes: new Map(),

    vehicleChanges: new Map(),

    setLoadingConflicts: (loadingConflicts) => {

        set(state => {

            if (state.loadingConflicts === loadingConflicts) {
                return state;
            }

            return { loadingConflicts };

        });

    },

    createRoot: (payload) => {

        if (!payload?.tripId) {
            return;
        }

        set(state => {

            const nodes = new Map(state.nodes);

            const node = createNode(payload);

            const current = nodes.get(payload.tripId);

            if (sameNode(current, node)) {
                return state;
            }

            nodes.set(
                payload.tripId,
                node
            );

            return {
                nodes
            };

        });

    },

    removeRoot: (tripId) => {

        if (!tripId) {
            return;
        }

        set(state => {

            if (!state.nodes.has(tripId)) {
                return state;
            }

            const nodes = new Map(state.nodes);

            nodes.delete(tripId);

            return {
                nodes
            };

        });

    },

    addChildren: (
        tripId,
        conflicts = []
    ) => {

        if (!tripId) {
            return;
        }

        set(state => {
            
            const parent = state.nodes.get(tripId);

            if (!parent) {
                return state;
            }

            const nodes = new Map(state.nodes);

            let changed = false;

            const children = [];

            for (const conflict of conflicts) {

                children.push(conflict.tripId);

                const current = nodes.get(conflict.tripId);

                const node = createNode({

                    tripId:
                        conflict.tripId,

                    parentTripId: tripId,

                    codigoIntegracao:
                        conflict.codigoIntegracao,

                    originalVehicle:
                        conflict.vehicle,

                    newVehicle:
                        current?.newVehicle ??
                        conflict.vehicle,

                    startAt:
                        conflict.startAt,

                    endAt:
                        conflict.endAt,

                    timeCaught:
                        conflict.timeCaught,

                    canAdjustNow:
                        conflict.canAdjustNow,

                    editing:
                        conflict.editing

                });

                node.resolved = current?.resolved ?? false;

                node.children = current?.children ?? [];

                if (sameNode(current, node)) {
                    continue;
                }

                nodes.set(
                    conflict.tripId,
                    node
                );

                changed = true;

            }

            if (
                !sameChildren(
                    parent.children,
                    children
                )
            ) {

                nodes.set(
                    tripId,
                    {
                        ...parent,
                        children
                    }
                );

                changed = true;

            }

            if (!changed) {
                return state;
            }

            return {
                nodes
            };

        });

    },

    setVehicleChange: (
        tripId,
        originalVehicle,
        newVehicle,
        startAt = null,
        codigoIntegracao = null
    ) => {

        if (!tripId) {
            return;
        }

        set(state => {

            const vehicleChanges = new Map(
                state.vehicleChanges
            );

            if (
                !originalVehicle ||
                !newVehicle ||
                originalVehicle === newVehicle
            ) {

                vehicleChanges.delete(tripId);

            } else {

                const current =
                    vehicleChanges.get(tripId);

                if (
                    current &&
                    current.originalVehicle === originalVehicle &&
                    current.newVehicle === newVehicle
                ) {
                    return state;
                }

                vehicleChanges.set(
                    tripId,
                    {
                        tripId,
                        originalVehicle,
                        newVehicle,
                        startAt,
                        codigoIntegracao
                    }
                );

            }

            return {
                vehicleChanges
            };

        });

    },

    isVehicleUsed: (plate) => {

        if (!plate) {
            return false;
        }

        for (const node of get().nodes.values()) {

            if (
                node.originalVehicle === plate ||
                node.newVehicle === plate
            ) {
                return true;
            }

        }

        return false;

    },

    updateVehicle: (
        tripId,
        newVehicle
    ) => {

        if (!tripId) {
            return;
        }

        set(state => {

            const current = state.nodes.get(tripId);

            if (!current) {
                return state;
            }

            if (current.newVehicle === newVehicle) {
                return state;
            }

            const nodes = new Map(state.nodes);

            nodes.set(
                tripId,
                {
                    ...current,
                    newVehicle:
                        current.originalVehicle === newVehicle
                            ? null
                            : newVehicle
                }
            );

            return {
                nodes
            };

        });

    },

    getNode: (tripId) => {

        if (!tripId) {
            return null;
        }

        return get().nodes.get(tripId) ?? null;

    },

    updateNode: (
        tripId,
        updater
    ) => {

        set(state => {

            const current = state.nodes.get(tripId);

            const updated = updater(current);

            if (updated === current) {
                return state;
            }

            const nodes = new Map(state.nodes);

            nodes.set(
                tripId,
                updated
            );

            return {
                nodes
            };

        });

    },

    setResolved: (
        tripId,
        resolved
    ) => {

        get().updateNode(
            tripId,
            node => {

                if (node.resolved === resolved) {
                    return node;
                }

                return {
                    ...node,
                    resolved
                };

            }
        );

    },

    setVehicleChangeMotive: (vehicleChangeMotive) => {
        set(state => {
            if (state.vehicleChangeMotive === vehicleChangeMotive) {
                return state;
            }

            return { vehicleChangeMotive };
        });
    },

    clearTree: () => {
        set(state => {

            if (
                !state.loadingConflicts &&
                state.nodes.size === 0
            ) {
                return state;
            }

            return {
                loadingConflicts: false,
                nodes: new Map()
            };

        });
    },

    clearAll: () => {
        set(state => {

            if (
                !state.loadingConflicts &&
                state.nodes.size === 0 &&
                state.vehicleChanges.size === 0
            ) {
                return state;
            }

            return {
                loadingConflicts: false,
                nodes: new Map(),
                vehicleChanges: new Map()
            };

        });
    },

    hasConflictTree: () => {

        return get().nodes.size > 0;

    },

    getRootNode: () => {

        for (const node of get().nodes.values()) {

            // raiz = primeiro nó cujo tripId é string
            if (typeof node.tripId === 'string') {
                return node;
            }

        }

        return null;

    },

    isResolved: (tripId) => {

        const node = get().getNode(tripId);

        if (!node) {
            return false;
        }

        if (!node.resolved) {
            return false;
        }

        for (const childId of node.children) {

            if (!get().isResolved(childId)) {
                return false;
            }

        }

        return true;

    },

    updateResolvedUpwards: (tripId) => {

        let current = get().getNode(tripId);

        while (current?.parentTripId) {

            const parent = get().getNode(
                current.parentTripId
            );

            if (!parent) {
                return;
            }

            let resolved = true;

            for (const childId of parent.children) {

                if (!get().isResolved(childId)) {
                    resolved = false;
                    break;
                }

            }

            get().setResolved(
                parent.tripId,
                resolved
            );

            current = parent;

        }

    },

    allResolved: () => {

        const root = get().getRootNode();

        if (!root) {
            return true;
        }

        return get().isResolved(root.tripId);

    },

    hasUndoChanges: () => {

        const root = get().getRootNode();

        for (const change of get().vehicleChanges.values()) {

            if (change.tripId !== root?.tripId) {
                return true;
            }

        }

        return false;

    },

    resetNodeVehicle: (tripId) => {

        get().updateNode(
            tripId,
            node => ({

                ...node,

                newVehicle: null,

                resolved: false,

                children: []

            })
        );

    },

    undoConflictChanges: () => {

        const root = get().getRootNode();

        if (!root) {
            return;
        }

        set(state => {

            const vehicleChanges = new Map(
                state.vehicleChanges
            );

            for (const change of vehicleChanges.values()) {

                if (change.tripId === root.tripId) {
                    continue;
                }

                get().resetNodeVehicle(change.tripId);
                get().updateResolvedUpwards(change.tripId);
                vehicleChanges.delete(change.tripId);

            }

            return {
                vehicleChanges
            };

        });

    },

    hasBlockedConflicts: () => {

        for (const node of get().nodes.values()) {

            if (!node.canAdjustNow) {
                return true;
            }

        }

        return false;

    },
    
    exportState: () => {

        const {
            nodes,
            vehicleChanges
        } = get();

        return {
            nodes,
            vehicleChanges
        };

    },

    importState: ({
        nodes,
        vehicleChanges
    }) => {

        set({
            nodes,
            vehicleChanges
        });

    },

}));